import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  FileText, User, Building2, Image as ImageIcon, PenLine, Type,
  CheckCircle2, AlertTriangle, Send, Plus, Trash2, Lock, Eraser, Loader2,
  Phone, Mail, MapPin, Eye, Download,
} from "lucide-react";
import ContractFullText from "../../components/contracts/ContractFullText";
import { useCompany } from "../../context/CompanyContext";

/**
 * Public, token-protected page for the client to review + sign a digital
 * contract. Does NOT require login — the unique token in the URL grants
 * read+update+sign access until the contract is signed.
 *
 * Flow on this page:
 *   1. Auto-fills from the database (client + properties).
 *   2. Client edits identification, selects properties or "global scope".
 *   3. Client picks image authorization (yes / no).
 *   4. Client ticks the terms checkbox.
 *   5. Client signs (drawn canvas OR typed name).
 *   6. Submit → backend generates PDF, emails both parties, marks signed.
 */

// We use a bare axios instance with NO credentials/cookies — the token in
// the URL is the only auth artefact this page needs.
const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const http = axios.create({ baseURL: `${API}/api`, withCredentials: false });


function Section({ title, icon, children, testId }) {
  return (
    <section data-testid={testId}
             className="rounded-2xl border border-[#1a4640] bg-[#062c27] overflow-hidden">
      <header className="flex items-center gap-2.5 px-5 sm:px-6 py-4 border-b border-[#1a4640]">
        <span className="text-[#d4af37] shrink-0">{icon}</span>
        <h2 className="font-display text-base sm:text-lg text-[#fdfbf7]">{title}</h2>
      </header>
      <div className="p-5 sm:p-6">{children}</div>
    </section>
  );
}

function FormField({ label, value, onChange, type = "text", testId, full, icon, placeholder, readOnly }) {
  return (
    <label className={`block ${full ? "sm:col-span-2" : ""}`}>
      <span className="block text-[10px] uppercase tracking-wider text-[#a0b2af] mb-1.5 inline-flex items-center gap-1.5">
        {icon} {label}
      </span>
      <input data-testid={testId}
             type={type}
             value={value ?? ""}
             placeholder={placeholder}
             readOnly={readOnly}
             onChange={e => onChange(e.target.value)}
             className={`w-full px-3 py-2.5 rounded-md border text-sm text-[#fdfbf7] placeholder:text-[#a0b2af] focus:outline-none transition-colors ${
               readOnly
                 ? "bg-[#062c27]/60 border-[#1a4640] cursor-not-allowed text-[#cfd8d6]"
                 : "bg-[#041f1b] border-[#1a4640] focus:border-[#d4af37]"
             }`} />
    </label>
  );
}


/* ── Signature pad ─────────────────────────────────────────────────── */
function SignaturePad({ method, onChange, value }) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const [empty, setEmpty] = useState(true);

  // Set up the canvas on mount + on resize
  useEffect(() => {
    if (method !== "drawn") return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    function resize() {
      const ratio = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * ratio;
      canvas.height = rect.height * ratio;
      const ctx = canvas.getContext("2d");
      ctx.scale(ratio, ratio);
      ctx.lineWidth = 2.2;
      ctx.lineCap = "round";
      ctx.strokeStyle = "#fdfbf7";
    }
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [method]);

  function pos(e) {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  }
  function down(e) {
    e.preventDefault();
    drawingRef.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  }
  function move(e) {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    if (empty) setEmpty(false);
  }
  function up() {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    onChange(canvasRef.current.toDataURL("image/png"));
  }
  function clear() {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    setEmpty(true);
    onChange("");
  }

  if (method === "typed") {
    return (
      <div>
        <input
          data-testid="signature-typed"
          type="text"
          value={value || ""}
          onChange={e => onChange(e.target.value)}
          placeholder="Escreva o seu nome completo…"
          className="w-full px-4 py-4 rounded-md bg-[#041f1b] border-2 border-[#d4af37]/40 text-2xl font-serif italic text-[#fdfbf7] focus:border-[#d4af37] focus:outline-none"
          style={{ fontFamily: "'Brush Script MT', cursive" }}
        />
        <p className="mt-2 text-[11px] text-[#a0b2af]">
          O seu nome aparecerá como assinatura caligráfica no PDF final.
        </p>
      </div>
    );
  }
  return (
    <div>
      <div className="rounded-md border-2 border-[#d4af37]/40 bg-[#041f1b] overflow-hidden">
        <canvas
          ref={canvasRef}
          data-testid="signature-canvas"
          onMouseDown={down} onMouseMove={move} onMouseUp={up} onMouseLeave={up}
          onTouchStart={down} onTouchMove={move} onTouchEnd={up}
          className="block w-full h-40 sm:h-48 cursor-crosshair touch-none"
        />
      </div>
      <div className="mt-2 flex items-center justify-between">
        <p className="text-[11px] text-[#a0b2af]">Desenhe a sua assinatura com o dedo ou rato.</p>
        <button onClick={clear}
                type="button"
                data-testid="signature-clear"
                className="inline-flex items-center gap-1.5 text-xs text-[#a0b2af] hover:text-[#d4af37] transition-colors">
          <Eraser size={12} /> Limpar
        </button>
      </div>
    </div>
  );
}


export default function ContractSignPage() {
  const { company, logoDark } = useCompany();
  const { token } = useParams();
  const navigate = useNavigate();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sigMethod, setSigMethod] = useState("drawn");
  const [sigData, setSigData] = useState("");
  const debounceRef = useRef(null);

  async function load() {
    setLoading(true);
    try {
      const { data } = await http.get(`/contracts/sign/${token}`);
      setContract(data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Link inválido ou contrato não encontrado.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  // Autosave on field change (debounced 700ms)
  const persist = useCallback(async (patch) => {
    setSaving(true);
    try {
      const { data } = await http.put(`/contracts/sign/${token}`, patch);
      setContract(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao guardar");
    } finally {
      setSaving(false);
    }
  }, [token]);
  function patch(field, value) {
    setContract(prev => ({ ...prev, [field]: value }));
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => persist({ [field]: value }), 700);
  }

  function setClientField(k, v) {
    setContract(prev => ({ ...prev, client: { ...(prev.client || {}), [k]: v } }));
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => persist({ client: { ...(contract?.client || {}), [k]: v } }), 700);
  }

  function toggleProperty(idx) {
    const next = (contract.properties || []).map((p, i) =>
      i === idx ? { ...p, included: !p.included } : p
    );
    patch("properties", next);
  }
  function addProperty() {
    const next = [...(contract.properties || []), { id: null, name: "", address: "", code: "", included: true }];
    patch("properties", next);
  }
  function removeProperty(idx) {
    const next = (contract.properties || []).filter((_, i) => i !== idx);
    patch("properties", next);
  }
  function updateProperty(idx, key, value) {
    const next = (contract.properties || []).map((p, i) =>
      i === idx ? { ...p, [key]: value } : p
    );
    patch("properties", next);
  }

  async function submit() {
    if (!sigData) {
      toast.error("Por favor assine antes de submeter.");
      return;
    }
    setSubmitting(true);
    try {
      // Make sure last edit is flushed
      clearTimeout(debounceRef.current);
      await http.post(`/contracts/sign/${token}/sign`, {
        method: sigMethod, data: sigData,
      });
      toast.success("Contrato assinado com sucesso!");
      navigate(`/contratos/assinar/${token}/concluido`, { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao assinar");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#041f1b] grid place-items-center">
        <Loader2 className="text-[#d4af37] animate-spin" size={32} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-[#041f1b] grid place-items-center px-4">
        <div className="max-w-md text-center">
          <AlertTriangle className="text-amber-400 mx-auto" size={36} />
          <h1 className="mt-4 font-display text-2xl text-[#fdfbf7]">Não foi possível abrir</h1>
          <p className="mt-2 text-sm text-[#a0b2af]">{error}</p>
        </div>
      </div>
    );
  }
  if (!contract) return null;

  // Already-signed splash
  if (contract.status === "signed") {
    return (
      <div className="min-h-screen bg-[#041f1b] grid place-items-center px-4">
        <div className="max-w-md text-center" data-testid="contract-already-signed">
          <CheckCircle2 className="text-emerald-400 mx-auto" size={42} />
          <h1 className="mt-5 font-display text-3xl text-[#fdfbf7]">Contrato assinado ✓</h1>
          <p className="mt-3 text-sm text-[#a0b2af]">
            Já foi assinado em {new Date(contract.signed_at).toLocaleString("pt-PT")}.
            Receberá uma cópia em PDF por email.
          </p>
        </div>
      </div>
    );
  }

  // Gating logic
  const props = contract.properties || [];
  const hasGlobal = !!contract.global_scope;
  const hasSelectedProps = hasGlobal || props.some(p => p.included);
  const imgAuth = contract.image_authorization;
  const accepted = !!contract.accepted_terms;
  const dataConfirmed = !!contract.data_confirmed;
  const canSign = dataConfirmed && hasSelectedProps && imgAuth && accepted && !!sigData;

  // Step 1 → confirm client identity. Required fields filled?
  const cl = contract.client || {};
  const clientReady =
    !!(cl.name && cl.name.trim()) &&
    !!(cl.email && cl.email.trim()) &&
    !!(cl.nif && cl.nif.trim());

  async function confirmData() {
    setSubmitting(true);
    try {
      // Flush any pending edits first
      clearTimeout(debounceRef.current);
      const { data } = await http.post(`/contracts/sign/${token}/confirm-data`, {
        client: contract.client || {},
      });
      setContract(data);
      toast.success("Dados confirmados ✓");
      // Smooth scroll to next section
      setTimeout(() => {
        document.querySelector("[data-testid=section-properties]")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao confirmar dados");
    } finally {
      setSubmitting(false);
    }
  }

  async function reopenDataEdit() {
    // Allow client to come back and fix data — we just unset the flag
    // locally; once they hit "Confirmar" again it persists.
    setContract(prev => ({ ...prev, data_confirmed: false }));
    setTimeout(() => {
      document.querySelector("[data-testid=section-client]")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  }

  return (
    <div className="min-h-screen bg-[#041f1b] text-[#fdfbf7] antialiased">
      <header className="sticky top-0 z-30 bg-[#041f1b]/95 backdrop-blur border-b border-[#1a4640]">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="inline-flex items-center gap-2">
            <span className="bg-[#fdfbf7] rounded-sm px-2 py-1 inline-flex items-center">
              <img src={logoDark} alt={company.trade_name} className="h-10 w-auto" />
            </span>
            <span className="text-[10px] uppercase tracking-[0.18em] text-[#a0b2af]">
              Contrato digital
            </span>
          </div>
          <div className="text-xs inline-flex items-center gap-1.5">
            {saving ? (
              <span className="text-[#d4af37] inline-flex items-center gap-1.5">
                <Loader2 size={11} className="animate-spin" /> A guardar
              </span>
            ) : (
              <span className="text-[#a0b2af] inline-flex items-center gap-1.5">
                <Lock size={11} className="text-emerald-400" /> Sessão segura
              </span>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-6 pb-32"
            data-testid="contract-sign-page">
        <div>
          <span className="inline-block text-[10px] uppercase tracking-[0.22em] text-[#d4af37]">
            Contrato de Prestação de Serviços
          </span>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl text-[#fdfbf7]">
            Bem-vindo, <span className="italic text-[#d4af37]">{contract.client?.name?.split(" ")[0] || "Cliente"}</span>
          </h1>
          <p className="mt-3 text-sm text-[#a0b2af] leading-relaxed">
            Reveja os dados, selecione as propriedades, indique a sua decisão sobre a
            utilização de imagens e assine para concluir. Demora cerca de 2 minutos.
          </p>
        </div>

        <Section title="Dados do Cliente" icon={<User size={18} />} testId="section-client">
          <div className="mb-4 rounded-md border border-[#d4af37]/30 bg-[#d4af37]/5 p-3 flex items-start gap-2">
            <CheckCircle2 size={14} className="text-[#d4af37] mt-0.5 shrink-0" />
            <p className="text-[12px] text-[#cfd8d6] leading-relaxed">
              Os seus dados foram pré-preenchidos. <strong className="text-[#fdfbf7]">Reveja com atenção</strong> e
              edite o que estiver incorreto. Depois clique em <em>Confirmar dados</em> para continuar.
              Os dados confirmados ficarão registados no contrato.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label="Nome completo" testId="contract-name" icon={<User size={11} />}
                       readOnly={dataConfirmed}
                       value={contract.client?.name} onChange={v => setClientField("name", v)} />
            <FormField label="NIF" testId="contract-nif"
                       readOnly={dataConfirmed}
                       value={contract.client?.nif} onChange={v => setClientField("nif", v)} />
            <FormField label="Email" testId="contract-email" type="email" icon={<Mail size={11} />}
                       readOnly={dataConfirmed}
                       value={contract.client?.email} onChange={v => setClientField("email", v)} />
            <FormField label="Telefone" testId="contract-phone" type="tel" icon={<Phone size={11} />}
                       readOnly={dataConfirmed}
                       value={contract.client?.phone} onChange={v => setClientField("phone", v)} />
            <FormField label="Morada" testId="contract-address" full icon={<MapPin size={11} />}
                       readOnly={dataConfirmed}
                       value={contract.client?.address} onChange={v => setClientField("address", v)} />
          </div>
          {!dataConfirmed ? (
            <div className="mt-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-4 border-t border-[#1a4640]">
              <p className="text-[11px] text-[#a0b2af]">
                {clientReady
                  ? "Tudo pronto. Pode confirmar os dados para continuar."
                  : "Preencha pelo menos nome, email e NIF para confirmar."}
              </p>
              <button onClick={confirmData}
                      disabled={!clientReady || submitting}
                      data-testid="contract-confirm-data"
                      className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md text-sm font-semibold uppercase tracking-wider bg-[#d4af37] text-[#041f1b] hover:bg-[#e8c860] hover:shadow-lg hover:shadow-[#d4af37]/40 transition-all disabled:opacity-40 disabled:hover:translate-y-0 shrink-0">
                <CheckCircle2 size={14} /> {submitting ? "A confirmar…" : "Confirmar dados e continuar"}
              </button>
            </div>
          ) : (
            <div className="mt-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-4 border-t border-emerald-500/30">
              <p className="text-[12px] text-emerald-300 inline-flex items-center gap-1.5"
                 data-testid="contract-data-confirmed-badge">
                <CheckCircle2 size={14} /> Dados confirmados. Pode prosseguir abaixo.
              </p>
              <button onClick={reopenDataEdit} type="button"
                      data-testid="contract-reopen-data"
                      className="text-[11px] uppercase tracking-wider text-[#a0b2af] hover:text-[#d4af37] transition-colors underline-offset-4 hover:underline">
                Editar dados
              </button>
            </div>
          )}
        </Section>

        {!dataConfirmed && (
          <div className="rounded-xl border border-dashed border-[#1a4640] bg-[#062c27]/50 p-8 text-center"
               data-testid="contract-locked-notice">
            <Lock size={28} className="mx-auto text-[#1a4640]" />
            <p className="mt-3 text-sm text-[#7c8e8b] italic">
              Confirme os seus dados acima para revelar o restante do contrato.
            </p>
          </div>
        )}

        <div className={`space-y-6 ${dataConfirmed ? "" : "hidden"}`}
             data-testid="contract-after-confirm">

        <Section title="Propriedades Associadas" icon={<Building2 size={18} />} testId="section-properties">
          <label className="flex items-start gap-3 mb-5 p-3 rounded-lg border border-[#d4af37]/30 bg-[#d4af37]/5 cursor-pointer">
            <input type="checkbox"
                   data-testid="contract-global-scope"
                   checked={!!contract.global_scope}
                   onChange={e => patch("global_scope", e.target.checked)}
                   className="mt-1 w-4 h-4 accent-[#d4af37]" />
            <span>
              <span className="block text-sm text-[#fdfbf7] font-medium">
                Aplicar a <em>todas</em> as propriedades atuais e futuras
              </span>
              <span className="block text-xs text-[#a0b2af] mt-1">
                Novas propriedades adicionadas no futuro ficam automaticamente abrangidas.
              </span>
            </span>
          </label>

          {!contract.global_scope && (
            <>
              {props.length === 0 ? (
                <p className="text-sm text-[#a0b2af] italic">
                  Não temos propriedades pré-registadas. Adicione manualmente abaixo.
                </p>
              ) : (
                <ul className="space-y-2.5">
                  {props.map((p, i) => (
                    <li key={i}
                        data-testid={`contract-property-${i}`}
                        className={`rounded-lg border p-3 transition-colors ${
                          p.included ? "border-[#d4af37]/40 bg-[#d4af37]/5" : "border-[#1a4640] bg-[#041f1b]"
                        }`}>
                      <div className="flex items-start gap-3">
                        <input type="checkbox"
                               data-testid={`contract-property-toggle-${i}`}
                               checked={!!p.included}
                               onChange={() => toggleProperty(i)}
                               className="mt-1 w-4 h-4 accent-[#d4af37] shrink-0" />
                        <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-2">
                          <input type="text" value={p.name || ""}
                                 onChange={e => updateProperty(i, "name", e.target.value)}
                                 placeholder="Nome"
                                 className="px-2 py-1.5 text-sm rounded-md bg-[#041f1b] border border-[#1a4640] text-[#fdfbf7] focus:border-[#d4af37] focus:outline-none" />
                          <input type="text" value={p.code || ""}
                                 onChange={e => updateProperty(i, "code", e.target.value)}
                                 placeholder="Código"
                                 className="px-2 py-1.5 text-sm rounded-md bg-[#041f1b] border border-[#1a4640] text-[#fdfbf7] focus:border-[#d4af37] focus:outline-none" />
                          <input type="text" value={p.address || ""}
                                 onChange={e => updateProperty(i, "address", e.target.value)}
                                 placeholder="Morada"
                                 className="px-2 py-1.5 text-sm rounded-md bg-[#041f1b] border border-[#1a4640] text-[#fdfbf7] focus:border-[#d4af37] focus:outline-none" />
                        </div>
                        <button onClick={() => removeProperty(i)}
                                data-testid={`contract-property-remove-${i}`}
                                className="p-1.5 rounded-md text-red-400 hover:bg-red-500/10 shrink-0">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              <button onClick={addProperty}
                      data-testid="contract-add-property"
                      className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md text-xs font-semibold uppercase tracking-wider border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37]/10 transition-all">
                <Plus size={14} /> Adicionar propriedade
              </button>
            </>
          )}

          {!hasSelectedProps && (
            <p className="mt-4 text-xs text-amber-400 inline-flex items-center gap-1.5">
              <AlertTriangle size={12} /> Selecione pelo menos uma propriedade ou active o âmbito global.
            </p>
          )}
        </Section>

        <Section title="Serviços Contratados" icon={<FileText size={18} />} testId="section-services">
          <p className="text-sm text-[#a0b2af] mb-4">Resumo dos serviços incluídos (descrição completa nas cláusulas abaixo):</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {[
              ["cleaning", "Limpeza"], ["supervision", "Supervisão"],
              ["checkin", "Check-in"], ["laundry", "Lavandaria"],
              ["reports", "Relatórios"],
            ].map(([k, label]) => (
              <span key={k} className={`px-3 py-2 rounded-full text-xs text-center font-medium border ${
                contract.services?.[k]
                  ? "bg-[#d4af37]/10 border-[#d4af37]/40 text-[#d4af37]"
                  : "bg-[#041f1b] border-[#1a4640] text-[#a0b2af]"
              }`}>
                {contract.services?.[k] ? "✓" : "—"} {label}
              </span>
            ))}
          </div>
        </Section>

        <Section title="Autorização de Utilização de Imagens" icon={<ImageIcon size={18} />} testId="section-image-auth">
          <p className="text-sm text-[#a0b2af] mb-4">
            Toma conhecimento e <strong className="text-[#fdfbf7]/90">autoriza ou não autoriza</strong>{" "}
            a utilização de imagens da propriedade para fins promocionais da {company.trade_name}:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              ["yes", "AUTORIZO", "Permito o uso das imagens", "emerald"],
              ["no", "NÃO AUTORIZO", "As minhas imagens são privadas", "red"],
            ].map(([key, label, desc, tone]) => (
              <button key={key}
                      type="button"
                      data-testid={`contract-image-auth-${key}`}
                      onClick={() => patch("image_authorization", key)}
                      className={`text-left rounded-lg border-2 p-4 transition-all ${
                        imgAuth === key
                          ? (tone === "emerald" ? "border-emerald-500 bg-emerald-500/10" : "border-red-500 bg-red-500/10")
                          : "border-[#1a4640] bg-[#041f1b] hover:border-[#d4af37]/40"
                      }`}>
                <div className="flex items-center gap-2">
                  <span className={`w-4 h-4 rounded-full border-2 ${
                    imgAuth === key
                      ? (tone === "emerald" ? "border-emerald-500 bg-emerald-500" : "border-red-500 bg-red-500")
                      : "border-[#a0b2af]"
                  }`} />
                  <span className="font-display text-sm text-[#fdfbf7]">{label}</span>
                </div>
                <p className="mt-2 text-xs text-[#a0b2af] pl-6">{desc}</p>
              </button>
            ))}
          </div>
        </Section>

        <Section title="Texto Completo do Contrato" icon={<FileText size={18} />} testId="section-full-text">
          <p className="text-sm text-[#a0b2af] mb-4">
            Leia integralmente as cláusulas abaixo antes de assinar. Pode também abrir
            ou descarregar o documento em PDF para o consultar com calma.
          </p>
          <div className="flex flex-wrap gap-2 mb-5">
            <a href={`${API}/api/contracts/sign/${token}/pdf`}
               target="_blank" rel="noopener noreferrer"
               data-testid="contract-view-pdf"
               className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-[11px] font-semibold uppercase tracking-wider border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37]/10 transition-all">
              <Eye size={12} /> Ver contrato completo em PDF
            </a>
            <a href={`${API}/api/contracts/sign/${token}/pdf?download=true`}
               data-testid="contract-download-pdf"
               className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-[11px] font-semibold uppercase tracking-wider border border-[#1a4640] text-[#a0b2af] hover:border-[#d4af37]/40 hover:text-[#d4af37] transition-all">
              <Download size={12} /> Descarregar contrato
            </a>
          </div>
          <div className="rounded-lg border border-[#1a4640] bg-[#041f1b]/60 p-4 sm:p-5 max-h-[480px] overflow-y-auto"
               data-testid="contract-full-text-scroll">
            <ContractFullText contract={contract} />
          </div>
          <p className="mt-3 text-[11px] text-[#a0b2af] inline-flex items-center gap-1.5">
            <Lock size={11} /> O texto acima é a versão exata que ficará registada com a sua assinatura.
          </p>
        </Section>

        <Section title="Aceitação dos Termos" icon={<FileText size={18} />} testId="section-accept">
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox"
                   data-testid="contract-accept-terms"
                   checked={accepted}
                   onChange={e => patch("accepted_terms", e.target.checked)}
                   className="mt-1 w-5 h-5 accent-[#d4af37] shrink-0" />
            <span className="text-sm text-[#fdfbf7]/90 leading-relaxed">
              <strong className="text-[#fdfbf7]">Li e aceito</strong> os termos do contrato de
              prestação de serviços apresentado acima, incluindo todas as cláusulas (1.ª a 12.ª),
              a cláusula de cancelamento (5.ª) e a minha decisão sobre autorização de imagens (9.ª).
            </span>
          </label>
        </Section>

        <Section title="Assinatura Digital" icon={<PenLine size={18} />} testId="section-signature">
          <div className="flex gap-2 mb-4">
            <button type="button"
                    onClick={() => { setSigMethod("drawn"); setSigData(""); }}
                    data-testid="signature-method-drawn"
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-xs font-medium border transition-all ${
                      sigMethod === "drawn"
                        ? "bg-[#d4af37] text-[#041f1b] border-[#d4af37]"
                        : "bg-[#041f1b] border-[#1a4640] text-[#a0b2af] hover:border-[#d4af37]/50"
                    }`}>
              <PenLine size={12} /> Desenhar
            </button>
            <button type="button"
                    onClick={() => { setSigMethod("typed"); setSigData(""); }}
                    data-testid="signature-method-typed"
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-xs font-medium border transition-all ${
                      sigMethod === "typed"
                        ? "bg-[#d4af37] text-[#041f1b] border-[#d4af37]"
                        : "bg-[#041f1b] border-[#1a4640] text-[#a0b2af] hover:border-[#d4af37]/50"
                    }`}>
              <Type size={12} /> Escrever nome
            </button>
          </div>
          <SignaturePad method={sigMethod} value={sigData} onChange={setSigData} />
          <p className="mt-3 text-[11px] text-[#a0b2af] inline-flex items-center gap-1.5">
            <Lock size={11} /> Data, hora e IP serão registados automaticamente.
          </p>
        </Section>

        {/* Submit row — large primary action */}
        <div className="rounded-2xl border border-[#d4af37]/40 bg-gradient-to-br from-[#062c27] to-[#041f1b] p-6">
          {!canSign && (
            <ul className="mb-5 space-y-1.5 text-xs text-[#a0b2af]">
              {!hasSelectedProps && <li className="inline-flex items-center gap-1.5"><AlertTriangle size={12} className="text-amber-400" /> Selecione as propriedades</li>}
              {!imgAuth         && <li className="inline-flex items-center gap-1.5"><AlertTriangle size={12} className="text-amber-400" /> Indique a sua decisão sobre imagens</li>}
              {!accepted        && <li className="inline-flex items-center gap-1.5"><AlertTriangle size={12} className="text-amber-400" /> Aceite os termos</li>}
              {!sigData         && <li className="inline-flex items-center gap-1.5"><AlertTriangle size={12} className="text-amber-400" /> Assine acima</li>}
            </ul>
          )}
          <button onClick={submit}
                  disabled={!canSign || submitting}
                  data-testid="contract-submit"
                  className="w-full inline-flex items-center justify-center gap-2 px-5 py-4 rounded-md text-sm font-semibold uppercase tracking-wider bg-[#d4af37] text-[#041f1b] hover:bg-[#e8c860] hover:shadow-lg hover:shadow-[#d4af37]/40 transition-all disabled:opacity-40 disabled:hover:translate-y-0">
            <Send size={16} /> {submitting ? "A submeter…" : "Assinar contrato"}
          </button>
          <p className="mt-3 text-[11px] text-center text-[#a0b2af]">
            Receberá imediatamente uma cópia em PDF por email.
          </p>
        </div>

        </div> {/* /contract-after-confirm */}
      </main>
    </div>
  );
}
